// export default ComposePost;
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
	Image,
	Send,
	Video,
	Music,
	Mic,
	Camera as CameraIcon,
	Play,
	Pause,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useNewsfeed from "../../zustand/UseNewsfeed";
import { X, Heart, Award, Star, Flag, ArrowLeft } from "lucide-react";
import useUserStore from "../../zustand/UseUserStore";
import { newsfeedApi } from "../../api/newsFeedApi";
import { IMAGE_PATH } from "../../api/apiConfig";
import { userAuthAPI } from "../../api/userAuthAPI";
import Camera from "../Camera";
import CaptureMethodSelector from "../CaptureMethodSelector";
import Waveform from "@/components/Waveform";

const ComposePost = ({
	onContestChange,
	selectedPostType,
	onPostTypeSelect,
	borderColor = "border-yellow-500",
	currentContestId = 0,
	onPostCreated,
	onClose,
}) => {
	const [prepRecord, setPrepRecord] = useState(false);
	const [mediaPreview, setMediaPreview] = useState(null);
	const [mediaFile, setMediaFile] = useState(null);
	const [recordingMediaFile, setRecordingMediaFile] = useState(null);
	const [mediaType, setMediaType] = useState(null);
	const [error, setError] = useState(null);
	const [isRecording, setIsRecording] = useState(false);
	const [recordingDuration, setRecordingDuration] = useState(0);
	const [isVideoRecording, setIsVideoRecording] = useState(false);
	const [isVideoSaving, setIsVideoSaving] = useState(false);
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [isPosting, setIsPosting] = useState(false);
	const [activeEvent, setActiveEvent] = useState(null);
	const [proudOptions, setProudOptions] = useState([]);
	const [selectedProudOption, setSelectedProudOption] = useState(null);
	const [showPostPreview, setShowPostPreview] = useState(false);
	const [isLoadingProudOptions, setIsLoadingProudOptions] = useState(false);
	const [isListening, setIsListening] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [showCaptureSelector, setShowCaptureSelector] = useState(false);
	const [showCamera, setShowCamera] = useState(false);
	const [cameraMode, setCameraMode] = useState("photo"); // 'photo' or 'video'
	const [autoTriggerUpload, setAutoTriggerUpload] = useState(false);

	const fileInputRef = useRef(null);
	const audioInputRef = useRef(null);
	const videoInputRef = useRef(null);
	const audioRecorderRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const mediaChunksRef = useRef([]);
	const recordingTimerRef = useRef(null);
	const videoRecorderRef = useRef(null);
	const videoRef = useRef(null);
	const audioContextRef = useRef(null);
	const analyserRef = useRef(null);
	const animationFrameRef = useRef(null);
	const audioElementRef = useRef(null);
	const playbackAnalyserRef = useRef(null);
	const syncTimeRef = useRef(null); // For smooth time updates
	const [audioLevels, setAudioLevels] = useState([]);
	const [isAudioPlaying, setIsAudioPlaying] = useState(false);
	const [audioDuration, setAudioDuration] = useState(0);
	const [audioCurrentTime, setAudioCurrentTime] = useState(0);
	const textareaRef = useRef(null);

	// Smoothly update audio current time at 60fps for smooth thumb movement
	useEffect(() => {
		if (isAudioPlaying && audioElementRef.current) {
			const updateTime = () => {
				if (audioElementRef.current && !audioElementRef.current.paused) {
					setAudioCurrentTime(audioElementRef.current.currentTime);
					syncTimeRef.current = requestAnimationFrame(updateTime);
				}
			};
			syncTimeRef.current = requestAnimationFrame(updateTime);
		}

		return () => {
			if (syncTimeRef.current) {
				cancelAnimationFrame(syncTimeRef.current);
				syncTimeRef.current = null;
			}
		};
	}, [isAudioPlaying]);
	const recognitionRef = useRef(null);
	const recognitionBaseContentRef = useRef(""); // Track content when recognition started
	const recognitionFinalTextRef = useRef(""); // Track what's been finalized so far

	// Zustand stores
	const newsfeed = useNewsfeed();
	const userStore = useUserStore();
	const navigate = useNavigate();
	const contests = [
		"Glow Game",
		...userStore
			.getContestNames()
			.filter((name) => name.toLowerCase() !== "the glow game"),
	];

	// Helper function to convert contest ID to index
	const getContestIndexFromID = (contestID) => {
		if (contestID === 0) {
			return 0;
		} // "Glow Game"

		// Find the contest role
		const contestRole = userStore.user_roles.find(
			(role) => role.ContestID === contestID
		);

		if (contestRole) {
			// Find the index in contests array
			const index = contests.findIndex(
				(c) => c.toLowerCase() === contestRole.ContestName.toLowerCase()
			);
			return index > 0 ? index : 0;
		}

		return 0;
	};

	// Calculate initial contest index from currentContestId prop
	// This is computed synchronously so there's no flash
	const initialContestIndex = useRef(getContestIndexFromID(currentContestId));
	const [contestIndex, setContestIndex] = useState(initialContestIndex.current);

	// Sync contestIndex with currentContestId prop (from Newsfeed filters)
	// currentContestId is a contest ID, not an index
	// This runs whenever currentContestId changes
	useEffect(() => {
		const indexFromID = getContestIndexFromID(currentContestId);
		if (indexFromID !== contestIndex) {
			console.log(
				"🔄 Syncing ComposePost contest - ID:",
				currentContestId,
				"Index:",
				indexFromID
			);
			setContestIndex(indexFromID);
		}
	}, [currentContestId, contests.length]); // Re-run if contests array changes too

	// Set initial contest to PrevContest on component mount
	// BUT only if currentContestId matches PrevContest (respect user's current selection)
	useEffect(() => {
		const prevContestID = userStore.user?.PrevContest;

		// Only set if:
		// 1. We have a PrevContest (including 0 for Glow Game)
		// 2. The currentContestId prop matches PrevContest (user's selection matches saved preference)
		// 3. The contestIndex doesn't match the PrevContest yet
		// This respects the user's saved preference while not overriding explicit selections
		if (
			prevContestID !== undefined &&
			prevContestID !== null &&
			currentContestId === prevContestID
		) {
			if (prevContestID === 0) {
				// Glow Game - ensure contestIndex is 0
				if (contestIndex !== 0) {
					console.log("Setting contest to Glow Game from PrevContest");
					setContestIndex(0);
					setActiveEvent(null);
				}
			} else {
				// Find the contest name from user roles
				const prevContestRole = userStore.user_roles.find(
					(role) => role.ContestID === prevContestID
				);

				if (prevContestRole) {
					// Find the index of this contest in the contests array
					const contestName = prevContestRole.ContestName;
					const index = contests.findIndex(
						(c) => c.toLowerCase() === contestName.toLowerCase()
					);

					if (index > 0 && index !== contestIndex) {
						// Skip index 0 which is "Glow Game"
						console.log(
							"Setting initial contest from PrevContest:",
							contestName,
							"at index:",
							index
						);
						setContestIndex(index);

						// Fetch active event for this contest
						newsfeedApi
							.getActiveEventForContest(prevContestID)
							.then((response) => {
								if (response.data?.result?.activeEvent) {
									setActiveEvent(response.data.result.activeEvent);
								}
							})
							.catch((error) => {
								console.error("Error fetching active event:", error);
							});
					}
				}
			}
		}
	}, [userStore.user_roles.length, currentContestId]); // Run when user roles are loaded or currentContestId changes

	// Handle contest change and notify parent component
	async function handleContestChange(index) {
		let newIndex;
		if (index < 0) {
			newIndex = contests.length - 1;
		} else if (index >= contests.length) {
			newIndex = 0;
		} else {
			newIndex = index;
		}
		setContestIndex(newIndex);

		// Auto-switch to "glow" post type when selecting a contest (not "Glow Game" or "The Glow Game")
		const contestName = contests[newIndex]?.toLowerCase() || "";
		if (contestName !== "glow game" && contestName !== "the glow game") {
			if (onPostTypeSelect) {
				onPostTypeSelect("glow");
			}
		} else {
			// Reset post type when switching to Glow Game to show all options
			if (onPostTypeSelect) {
				onPostTypeSelect(null);
			}
		}

		// Fetch active event for the selected contest and update PrevContest
		let contestid = null;
		if (contestName !== "glow game" && contestName !== "the glow game") {
			userStore.user_roles.forEach((role) => {
				if (role.ContestName === contests[newIndex]) {
					contestid = role.ContestID;
				}
			});

			if (contestid) {
				try {
					// Update member's PrevContest in the database
					await userAuthAPI.updateMember({
						MemberID: userStore.user?.MemberID,
						PrevContest: contestid,
					});
					console.log("✅ Updated PrevContest in database:", contestid);

					// Update Zustand store to keep state in sync
					const currentUser = useUserStore.getState().user;
					userStore.setUser({
						...currentUser,
						PrevContest: contestid,
					});
					console.log("✅ Updated PrevContest in Zustand store:", contestid);
				} catch (error) {
					console.error("Failed to update PrevContest:", error);
				}

				try {
					const response =
						await newsfeedApi.getActiveEventForContest(contestid);
					if (response.data?.result?.activeEvent) {
						setActiveEvent(response.data.result.activeEvent);
					} else {
						setActiveEvent(null);
					}
				} catch (error) {
					console.error("Error fetching active event:", error);
					setActiveEvent(null);
				}
			}
		} else {
			setActiveEvent(null);
			contestid = 0; // Glow Game

			// Save Glow Game (contestID 0) as PrevContest
			try {
				await userAuthAPI.updateMember({
					MemberID: userStore.user?.MemberID,
					PrevContest: 0,
				});
				console.log("✅ Updated PrevContest to Glow Game (0) in database");

				// Update Zustand store to keep state in sync
				const currentUser = useUserStore.getState().user;
				userStore.setUser({
					...currentUser,
					PrevContest: 0,
				});
				console.log("✅ Updated PrevContest to Glow Game (0) in Zustand store");
			} catch (error) {
				console.error("Failed to update PrevContest to Glow Game:", error);
			}
		}

		// Notify parent component of contest change with contest ID (not index!)
		if (onContestChange) {
			onContestChange(contestid);
		}
	}

	// Speech recognition functions
	const startSpeechRecognition = () => {
		// Check if browser supports speech recognition
		const SpeechRecognition =
			window.SpeechRecognition || window.webkitSpeechRecognition;
		if (!SpeechRecognition) {
			setError("Speech recognition is not supported in your browser.");
			return;
		}

		// Initialize recognition if not already done
		if (!recognitionRef.current) {
			recognitionRef.current = new SpeechRecognition();
			recognitionRef.current.continuous = true;
			recognitionRef.current.interimResults = true;
			recognitionRef.current.lang = "en-US";

			recognitionRef.current.onresult = (event) => {
				let interimTranscript = "";
				let newFinalTranscript = "";

				// Process all results from the last resultIndex
				for (let i = event.resultIndex; i < event.results.length; i++) {
					const { transcript } = event.results[i][0];
					if (event.results[i].isFinal) {
						// Only add new final text that hasn't been added yet
						newFinalTranscript += `${transcript} `;
					} else {
						// Track interim results separately
						interimTranscript += transcript;
					}
				}

				// Update the final text tracker
				if (newFinalTranscript) {
					recognitionFinalTextRef.current += newFinalTranscript;
				}

				// Update content: base + all finalized text + current interim
				setContent(() => {
					return (
						recognitionBaseContentRef.current +
						recognitionFinalTextRef.current +
						(interimTranscript ? ` ${interimTranscript}` : "")
					);
				});
			};

			recognitionRef.current.onerror = (event) => {
				console.error("Speech recognition error:", event.error);
				setIsListening(false);
				if (event.error === "not-allowed") {
					setError(
						"Microphone permission denied. Please allow microphone access."
					);
				} else if (event.error === "no-speech") {
					// This is fine, user just didn't speak
				} else {
					setError("Speech recognition error. Please try again.");
				}
			};

			recognitionRef.current.onend = () => {
				setIsListening(false);
				// Finalize any remaining content - preserve all recognized text
				setContent((currentContent) => {
					const baseContent = recognitionBaseContentRef.current || "";
					const finalText = recognitionFinalTextRef.current || "";
					const combined = (baseContent + finalText).trim();
					// Preserve the recognized text - only update if we have something
					if (combined) {
						return combined;
					}
					// If no recognized text, keep current content
					return currentContent;
				});
				// Don't reset refs yet - let them be cleared when new recognition starts
			};
		}

		// Store the current content as the baseline when recognition starts
		// If user has already typed something or there's existing content, preserve it
		recognitionBaseContentRef.current = content || "";
		recognitionFinalTextRef.current = "";

		try {
			recognitionRef.current.start();
			setIsListening(true);
			setError(null);
		} catch (error) {
			console.error("Error starting speech recognition:", error);
			setError("Failed to start speech recognition. Please try again.");
			setIsListening(false);
		}
	};

	const stopSpeechRecognition = () => {
		if (recognitionRef.current && isListening) {
			try {
				recognitionRef.current.stop();
			} catch (error) {
				console.error("Error stopping speech recognition:", error);
			}
		}
		setIsListening(false);
		// Finalize content immediately - preserve all recognized text
		setContent((currentContent) => {
			const baseContent = recognitionBaseContentRef.current || "";
			const finalText = recognitionFinalTextRef.current || "";
			const combined = (baseContent + finalText).trim();
			// Preserve the recognized text
			if (combined) {
				return combined;
			}
			// If no recognized text, keep current content
			return currentContent;
		});
		// Don't reset refs - the text is now in state, refs will be reset on next start
	};

	// Set up audio visualization for playback - only create Web Audio API once
	useEffect(() => {
		if (!mediaPreview || mediaType !== "audio" || !audioElementRef.current) {
			return;
		}

		const audioElement = audioElementRef.current;

		// Create audio context and analyser for playback (only once)
		try {
			if (!audioContextRef.current) {
				audioContextRef.current = new (window.AudioContext ||
					window.webkitAudioContext)();
			}

			if (!playbackAnalyserRef.current) {
				playbackAnalyserRef.current = audioContextRef.current.createAnalyser();
				playbackAnalyserRef.current.fftSize = 2048;
				playbackAnalyserRef.current.smoothingTimeConstant = 0.8; // Smooth out the waveform

				const source =
					audioContextRef.current.createMediaElementSource(audioElement);
				source.connect(playbackAnalyserRef.current);
				playbackAnalyserRef.current.connect(
					audioContextRef.current.destination
				);
			}
		} catch (error) {
			console.error("Error setting up audio visualization:", error);
		}

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
		};
	}, [mediaPreview, mediaType]);

	// Cleanup speech recognition on unmount
	useEffect(() => {
		return () => {
			if (recognitionRef.current) {
				try {
					recognitionRef.current.stop();
				} catch (error) {
					// Ignore errors on cleanup
				}
				recognitionRef.current = null;
			}
		};
	}, []);

	// Auto-trigger camera for glow/grateful posts immediately when modal opens
	// BUT NOT for contest submissions (user must click "Submit for Contest" button)
	useEffect(() => {
		const isContest =
			contests[contestIndex]?.toLowerCase() !== "glow game" &&
			contests[contestIndex]?.toLowerCase() !== "the glow game";

		const needsMedia =
			selectedPostType !== "pride" &&
			!mediaFile &&
			!mediaPreview &&
			!showCamera &&
			!showCaptureSelector &&
			!isContest; // Don't auto-trigger for contests

		if (needsMedia && selectedPostType) {
			// Open capture method selector
			setShowCaptureSelector(true);
		}
	}, [selectedPostType]); // Only run when post type changes

	// Handle capture method selection
	const handleCaptureMethodSelect = (method) => {
		setShowCaptureSelector(false);

		// Open camera with selected mode (or "photo" mode for upload)
		setCameraMode(method === "upload" ? "photo" : method);
		setAutoTriggerUpload(method === "upload");
		setShowCamera(true);
	};

	// Closes any active media
	function handleRemoveMedia() {
		// Revoke object URL for media preview
		if (mediaPreview && typeof mediaPreview === "string") {
			URL.revokeObjectURL(mediaPreview);
		}

		// Stop audio if playing
		if (audioElementRef.current) {
			audioElementRef.current.pause();
			audioElementRef.current.currentTime = 0;
		}

		// Clean up audio visualization
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		// Reset media state
		setMediaPreview(null);
		setMediaFile(null);
		setMediaType(null);
		setError(null);

		// Reset audio player state
		setIsAudioPlaying(false);
		setAudioDuration(0);
		setAudioCurrentTime(0);
		setAudioLevels([]);

		// Reset file inputs
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		if (audioInputRef.current) {
			audioInputRef.current.value = "";
		}
		if (videoInputRef.current) {
			videoInputRef.current.value = "";
		}
	}

	// Clears saved audio or video recordings
	function clearRecording() {
		// Stop any active recordings
		if (isRecording || isVideoRecording) {
			if (mediaRecorderRef.current) {
				mediaRecorderRef.current.stop();
			}

			// Stop all media tracks
			if (audioRecorderRef.current) {
				audioRecorderRef.current.getTracks().forEach((track) => track.stop());
			}
			if (videoRecorderRef.current) {
				videoRecorderRef.current.getTracks().forEach((track) => track.stop());
			}

			// Reset recording state
			setIsRecording(false);
			setIsVideoRecording(false);
			setRecordingDuration(0);

			// Clear recording timer
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
				recordingTimerRef.current = null;
			}
		}

		// Reset media preview
		if (mediaPreview && typeof mediaPreview === "string") {
			URL.revokeObjectURL(mediaPreview);
		}

		// Reset media state
		setMediaPreview(null);
		setMediaFile(null);
		setMediaType(null);

		// Reset error
		setError(null);
	}

	// Handles file selection
	function handleFileSelect(e, type) {
		const file = e.target.files[0];
		if (!file) {
			return;
		}

		// Clear previous error
		setError(null);

		// Create preview URL
		const previewUrl = URL.createObjectURL(file);
		setMediaPreview(previewUrl);
		setMediaFile(file);
		setMediaType(type);
	}

	// Handle camera capture
	const handleCameraCapture = (file) => {
		// Clear previous error
		setError(null);

		// Determine file type
		let type = "image";
		if (file.type.startsWith("video/")) {
			type = "video";
		} else if (file.type.startsWith("audio/")) {
			type = "audio";
		}

		// Create preview URL
		const previewUrl = URL.createObjectURL(file);
		setMediaPreview(previewUrl);
		setMediaFile(file);
		setMediaType(type);
	};

	// Starts audio recording
	async function startAudioRecording() {
		try {
			// Clear previous error
			setError(null);

			// Stop speech recognition if active
			if (isListening) {
				stopSpeechRecognition();
			}

			// Stop all active recordings
			stopAllRecordings();

			// Get user media stream
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			audioRecorderRef.current = stream;

			// Set up Web Audio API for visualization
			audioContextRef.current = new (window.AudioContext ||
				window.webkitAudioContext)();
			analyserRef.current = audioContextRef.current.createAnalyser();
			const source = audioContextRef.current.createMediaStreamSource(stream);
			source.connect(analyserRef.current);
			analyserRef.current.fftSize = 2048; // Higher FFT size for smoother waveform
			analyserRef.current.smoothingTimeConstant = 0.8; // Smooth out the waveform

			// Create media recorder
			mediaRecorderRef.current = new MediaRecorder(stream);
			mediaChunksRef.current = [];

			// When data available, add to mediaChunksRef
			mediaRecorderRef.current.ondataavailable = (e) => {
				if (e.data.size > 0) {
					mediaChunksRef.current.push(e.data);
				}
			};

			// When recording stops, create a blob and set media state
			mediaRecorderRef.current.onstop = () => {
				const audioBlob = new Blob(mediaChunksRef.current, {
					type: "audio/mpeg",
				});

				// Create object URL for audio preview
				const audioUrl = URL.createObjectURL(audioBlob);

				// Set media Preview
				setMediaPreview(audioUrl);
				setMediaFile(audioBlob);
				setMediaType("audio");

				// Stop all audio tracks
				audioRecorderRef.current.getTracks().forEach((track) => track.stop());

				// Clean up audio context
				if (audioContextRef.current) {
					audioContextRef.current.close();
					audioContextRef.current = null;
				}
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current);
					animationFrameRef.current = null;
				}
				setAudioLevels([]);
			};

			// Start recording
			mediaRecorderRef.current.start();
			setIsRecording(true);
			setRecordingDuration(0);

			// Start visualization loop
			const bufferLength = analyserRef.current.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			const updateVisualization = () => {
				if (
					!analyserRef.current ||
					!mediaRecorderRef.current ||
					mediaRecorderRef.current.state !== "recording"
				) {
					if (animationFrameRef.current) {
						cancelAnimationFrame(animationFrameRef.current);
						animationFrameRef.current = null;
					}
					return;
				}
				analyserRef.current.getByteFrequencyData(dataArray);
				// Get 20 bars for visualization (sample every nth value)
				const bars = 20;
				const step = Math.floor(bufferLength / bars);
				const levels = [];
				for (let i = 0; i < bars; i++) {
					const value = dataArray[i * step];
					levels.push(value);
				}
				setAudioLevels(levels);
				animationFrameRef.current = requestAnimationFrame(updateVisualization);
			};
			updateVisualization();
		} catch (error) {
			// Log error
			console.error("Error accessing microphone:", error);
			setError(
				"Could not access microphone. Please check your browser permissions."
			);
		}
	}

	// Stops audio recording
	function stopRecording() {
		// Stop audio recording
		if (mediaRecorderRef.current && (isRecording || isVideoRecording)) {
			mediaRecorderRef.current.stop();

			// Clean up audio context and animation
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setAudioLevels([]);

			// Reset recording state
			setIsRecording(false);
			setIsVideoRecording(false);
		}
	}

	// Starts video recording
	async function startVideoRecording() {
		try {
			setError(null);

			// Stop speech recognition if active
			if (isListening) {
				stopSpeechRecognition();
			}

			stopAllRecordings();

			// Get user media stream
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: true,
				video: {
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
			});

			// Set video recorder
			videoRecorderRef.current = stream;

			// Create media recorder
			mediaRecorderRef.current = new MediaRecorder(stream);
			mediaChunksRef.current = [];

			// Set up a live preview
			setMediaPreview(stream);
			setMediaType("video-recording");

			// Ensure the video element gets the stream immediately
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				videoRef.current.play().catch(console.error);
			}

			// When data available, add to mediaChunksRef
			mediaRecorderRef.current.ondataavailable = (e) => {
				if (isVideoSaving && e.data.size > 0) {
					mediaChunksRef.current.push(e.data);
				}
			};

			// When recording stops, create a blob and set media state | UNTESTED
			mediaRecorderRef.current.onstop = () => {
				// Create the final video blob when recording stops
				const videoBlob = new Blob(mediaChunksRef.current, {
					type: "video/mp4",
				});

				// Create a URL for the recorded video
				const recordedVideoUrl = URL.createObjectURL(videoBlob);
				setMediaPreview(recordedVideoUrl);
				setMediaFile(videoBlob);
				setRecordingMediaFile(videoBlob);
				setMediaType("video");

				// Stop all video tracks
				if (videoRecorderRef.current) {
					videoRecorderRef.current.getTracks().forEach((track) => track.stop());
				}
			};

			// Start recording
			setIsVideoRecording(true);
			setRecordingDuration(0);
		} catch (error) {
			// Log error
			console.error("Error accessing camera:", error);
			setError(
				"Could not access camera. Please check your browser permissions."
			);
		}
	}

	// Stops all active recordings
	function stopAllRecordings() {
		// Stop any active recordings
		if (isRecording || isVideoRecording) {
			if (mediaRecorderRef.current) {
				mediaRecorderRef.current.stop();
			}

			// Stop all media tracks
			if (audioRecorderRef.current) {
				audioRecorderRef.current.getTracks().forEach((track) => track.stop());
			}
			if (videoRecorderRef.current) {
				videoRecorderRef.current.getTracks().forEach((track) => track.stop());
			}

			// Clean up audio context and animation
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setAudioLevels([]);

			// Reset recording state
			setIsRecording(false);
			setIsVideoRecording(false);
			setRecordingDuration(0);

			// Clear recording timer
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
				recordingTimerRef.current = null;
			}
		}
	}

	// Handles recording timer
	useEffect(() => {
		// Start recording timer if recording is active
		if ((isRecording || isVideoRecording) && !recordingTimerRef.current) {
			recordingTimerRef.current = setInterval(() => {
				setRecordingDuration((prev) => prev + 1);
			}, 1000);
			// Stop recording timer if recording is stopped
		} else if (!isRecording && !isVideoRecording && recordingTimerRef.current) {
			clearInterval(recordingTimerRef.current);
			recordingTimerRef.current = null;
		}

		// Cleanup function
		return () => {
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
			}
		};
	}, [isRecording, isVideoRecording]);

	// Sets video srcObject when mediaPreview changes
	useEffect(() => {
		// Check if video element exists and mediaPreview is set
		if (videoRef.current && mediaPreview) {
			if (mediaType === "video-recording") {
				// For live recording, use srcObject
				videoRef.current.srcObject = mediaPreview;
				// Ensure the video plays
				videoRef.current.play().catch(console.error);
			} else if (mediaType === "video") {
				// For recorded video, use src
				videoRef.current.src = mediaPreview;
				videoRef.current.srcObject = null; // Clear srcObject
			}
		}
	}, [mediaPreview, mediaType]);

	// Auto-switch to "Glow Game" when grateful or proud post types are selected
	useEffect(() => {
		if (selectedPostType === "grateful" || selectedPostType === "pride") {
			const glowGameIndex = contests.findIndex(
				(contest) => contest.toLowerCase() === "glow game"
			);
			if (glowGameIndex !== -1 && glowGameIndex !== contestIndex) {
				handleContestChange(glowGameIndex);
			}
		}
	}, [selectedPostType, contests, contestIndex]);

	// Fetch active event when component loads
	useEffect(() => {
		const fetchInitialActiveEvent = async () => {
			const contestName = contests[contestIndex]?.toLowerCase() || "";
			if (contestName !== "glow game" && contestName !== "the glow game") {
				let contestid = null;
				userStore.user_roles.forEach((role) => {
					if (role.ContestName === contests[contestIndex]) {
						contestid = role.ContestID;
					}
				});

				if (contestid) {
					try {
						const response =
							await newsfeedApi.getActiveEventForContest(contestid);
						if (response.data?.result?.activeEvent) {
							setActiveEvent(response.data.result.activeEvent);
						} else {
							setActiveEvent(null);
						}
					} catch (error) {
						console.error("Error fetching active event:", error);
						setActiveEvent(null);
					}
				}
			}
		};

		fetchInitialActiveEvent();
	}, []); // Only run on mount

	// Fetch proud post options when pride post type is selected
	useEffect(() => {
		const fetchProudOptions = async () => {
			if (selectedPostType === "pride" && userStore.user?.MemberID) {
				setIsLoadingProudOptions(true);
				setError(null);
				try {
					const response = await newsfeedApi.getProudPostOptions(
						userStore.user.MemberID
					);
					console.log("Proud post options response:", response);

					// Transform the API response into selectable achievement objects
					const options = [];
					const result = response.data?.result;

					if (result?.achievements && result?.details) {
						const { achievements, details } = result;
						console.log("Achievements:", achievements);
						console.log("Details:", details);
						console.log("All achievement keys:", Object.keys(achievements));
						console.log("All details keys:", Object.keys(details));

						// Always show Secret Salutes with progress
						if (details.secretSalutes) {
							options.push({
								type: "secretSalutes",
								TotalDecks: details.secretSalutes.totalDecks,
								LumensEarned: details.secretSalutes.lumensEarned,
								Completed: details.secretSalutes.completed || 0,
								Total: details.secretSalutes.total || 0,
								IsComplete:
									achievements.completedAllSecretSalutes &&
									details.secretSalutes?.allDecksComplete,
							});
						}

						// Always show Star Salutes with progress
						if (details.starSalutes) {
							const starSaluteAchievementKeys = Object.keys(
								achievements
							).filter(
								(key) =>
									key.toLowerCase().includes("star") ||
									key.toLowerCase().includes("salute")
							);

							const hasSentFiveStarSalutes =
								achievements.sentFiveStarSalutes ||
								achievements.starSalutes ||
								achievements.sentStarSalutes ||
								achievements.fiveStarSalutes ||
								starSaluteAchievementKeys.some((key) => achievements[key]);

							const isComplete =
								hasSentFiveStarSalutes && details.starSalutes.sent >= 3;

							options.push({
								type: "star",
								SalutesSent: details.starSalutes.sent,
								Required: details.starSalutes.required,
								IsComplete: isComplete,
							});
						}

						// Always show Quests with progress
						if (details.quests) {
							const questDetails = details.quests.completedQuestDetails || [];
							const hasCompletedQuest =
								achievements.completedQuest && details.quests?.completedToday;

							if (hasCompletedQuest && questDetails.length > 0) {
								// If completed, show each completed quest separately
								questDetails.forEach((quest) => {
									options.push({
										type: "quest",
										QuestID: quest.questID,
										QuestName: quest.questName,
										MilestonesCompleted: quest.rewardedMilestones,
										TotalMilestones:
											quest.totalMilestones || quest.rewardedMilestones,
										CompletedDate: quest.lastMilestoneUpdated,
										IsComplete: true,
									});
								});
							} else {
								// Show progress for incomplete quests using quest milestones
								// Check for quest1Milestones, quest2Milestones, quest3Milestones, etc.
								const questMilestones =
									details.quests.quest1Milestones ||
									details.quests.quest2Milestones ||
									details.quests.quest3Milestones ||
									details.quests.quest4Milestones ||
									details.quests.quest5Milestones;

								if (questMilestones) {
									options.push({
										type: "quest",
										QuestName: "Daily Quest",
										MilestonesCompleted: questMilestones.completed || 0,
										TotalMilestones: questMilestones.total || 1,
										IsComplete: false,
									});
								} else {
									// Fallback if no quest milestones available
									options.push({
										type: "quest",
										QuestName: "Daily Quest",
										MilestonesCompleted: 0,
										TotalMilestones: 1,
										IsComplete: false,
									});
								}
							}
						}

						// Only add completed achievements below
						if (achievements.earnedCreature && details.creatures?.earnedToday) {
							const creaturesEarned = details.creatures.creaturesEarned || [];
							creaturesEarned.forEach((creature) => {
								options.push({
									type: "creature",
									CreatureNumber: creature.creatureNumber,
									CreatureName: creature.creatureName,
									ImageURL: creature.imageURL,
									IsComplete: true,
								});
							});
						}

						if (achievements.wonContest && details.contests?.wonToday) {
							const contestsWon = details.contests.contestsWon || [];
							contestsWon.forEach((contest) => {
								options.push({
									type: "contestWin",
									ContestName: contest.contestName || "Contest",
									ContestID: contest.contestID,
									IsComplete: true,
								});
							});
						}

						if (
							achievements.createdFirstGlow &&
							details.firstGlow?.createdToday
						) {
							options.push({
								type: "firstGlow",
								GlowID: details.firstGlow.glowDetails?.glowID,
								Title: details.firstGlow.glowDetails?.title,
								CreatedAt: details.firstGlow.glowDetails?.createdAt,
								LumensEarned: details.firstGlow.glowDetails?.lumens || 0,
								IsComplete: true,
							});
						}
					}

					console.log("Final options array:", options);
					setProudOptions(options);
				} catch (error) {
					console.error("Error fetching proud post options:", error);
					setError("Failed to load achievements. Please try again.");
					setProudOptions([]);
				} finally {
					setIsLoadingProudOptions(false);
				}
			} else {
				setProudOptions([]);
				setSelectedProudOption(null);
			}
		};

		fetchProudOptions();
	}, [selectedPostType, userStore.user?.MemberID]);

	// Handles preview button for proud posts
	const handleShowPreview = () => {
		if (selectedPostType === "pride" && selectedProudOption) {
			// Auto-populate body from selected achievement, but leave title empty
			const achievementBody = getAchievementBody(selectedProudOption);
			setTitle(""); // Keep title empty
			setContent(achievementBody);
			setShowPostPreview(true);
		}
	};

	// Handles post submission
	const handleSubmit = async () => {
		try {
			// Check if user has enough lumens for grateful posts
			if (selectedPostType === "grateful") {
				const currentLumens = userStore.user?.CurrentLumens || 0;
				if (currentLumens < 2) {
					setError("You need at least 2 lumens to post a grateful post.");
					return;
				}
			}

			setIsPosting(true);

			let contestid = null;
			userStore.user_roles.forEach((role) => {
				if (role.ContestName === contests[contestIndex]) {
					contestid = role.ContestID;
				}
			});

			// Submit post to newsfeed with post type
			console.log("📤 Submitting post with type:", selectedPostType);

			// For creature pride posts, fetch the creature image as a file
			let finalMediaFile = mediaFile;
			let finalMediaType = mediaType;

			if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "creature"
			) {
				try {
					// Map creature number to image path
					const creatureMap = {
						1: `${IMAGE_PATH}/quest/creatures/Jokeymon.png`,
						2: `${IMAGE_PATH}/quest/creatures/Patience.png`,
						3: `${IMAGE_PATH}/quest/creatures/Stevie.png`,
						4: `${IMAGE_PATH}/quest/creatures/bluekin.png`,
						5: `${IMAGE_PATH}/quest/creatures/lightkin.png`,
					};
					const creatureImagePath =
						creatureMap[selectedProudOption.CreatureNumber];
					console.log("🎨 Fetching creature image:", creatureImagePath);

					// Fetch the creature image and convert to blob
					const response = await fetch(creatureImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = creatureImagePath.split("/").pop();
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Creature image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading creature image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "star"
			) {
				try {
					// Use the stars image for star salute posts
					const starsImagePath = `${IMAGE_PATH}/newsfeed/stars.jpg`;
					console.log("⭐ Fetching stars image:", starsImagePath);

					// Fetch the stars image and convert to blob
					const response = await fetch(starsImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "stars.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Stars image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading stars image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "quest"
			) {
				try {
					// Use the quest complete image for quest completion posts
					const questCompleteImagePath = `${IMAGE_PATH}/newsfeed/questComplete.jpg`;
					console.log(
						"🏆 Fetching quest complete image:",
						questCompleteImagePath
					);

					// Fetch the quest complete image and convert to blob
					const response = await fetch(questCompleteImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "questComplete.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Quest complete image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading quest complete image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "secretSalutes"
			) {
				try {
					// Use the secret salutes image for secret salutes completion posts
					const secretSalutesImagePath = `${IMAGE_PATH}/newsfeed/secretSalutes.jpg`;
					console.log(
						"🎯 Fetching secret salutes image:",
						secretSalutesImagePath
					);

					// Fetch the secret salutes image and convert to blob
					const response = await fetch(secretSalutesImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "secretSalutes.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ Secret salutes image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading secret salutes image:", error);
					// Continue with post submission without the image
				}
			} else if (
				selectedPostType === "pride" &&
				selectedProudOption?.type === "firstGlow"
			) {
				try {
					// Use the first glow image for first glow post achievement
					const firstGlowImagePath = `${IMAGE_PATH}/newsfeed/firstglow.jpg`;
					console.log("✨ Fetching first glow image:", firstGlowImagePath);

					// Fetch the first glow image and convert to blob
					const response = await fetch(firstGlowImagePath);
					const blob = await response.blob();

					// Create a File object from the blob
					const fileName = "firstglow.jpg";
					finalMediaFile = new File([blob], fileName, { type: blob.type });
					finalMediaType = "image";

					console.log("✅ First glow image loaded as file:", {
						name: fileName,
						size: finalMediaFile.size,
						type: finalMediaFile.type,
					});
				} catch (error) {
					console.error("❌ Error loading first glow image:", error);
					// Continue with post submission without the image
				}
			}

			const result = await newsfeed.handleSubmitPost(
				title,
				content,
				finalMediaFile,
				finalMediaType,
				recordingDuration,
				contestid,
				0, // questID
				0, // memberMilestoneID
				selectedPostType || "glow" // postType - defaults to "glow"
			);
			console.log("✅ Post created:", result);

			// If post submission is successful
			if (result.success) {
				// Clear media after successful submission
				if (mediaFile) {
					handleRemoveMedia();
				} else if (recordingMediaFile) {
					clearRecording();
				}

				// Reset post content
				setContent("");
				setTitle("");
				setShowPostPreview(false);
				setSelectedProudOption(null);

				// Close the compose post modal/section
				if (onPostCreated) {
					onPostCreated();
				}
			}
		} catch (error) {
			console.error("Error submitting post:", error);
			setError("Failed to submit post. Please try again.");
		} finally {
			setIsPosting(false);
		}
	};

	// Helper function to get achievement title
	const getAchievementTitle = (achievement) => {
		// Determine the type of achievement and format accordingly
		switch (achievement.type) {
			case "quest":
				if (achievement.IsComplete) {
					return `Completed Quest: ${achievement.QuestName || "Quest"}`;
				}
				return achievement.QuestName || "Quest";
			case "star":
				if (achievement.IsComplete) {
					if (achievement.SalutesSent > achievement.Required) {
						return `Sent ${achievement.SalutesSent} Star Salutes - Above and Beyond!`;
					}
					return `Sent ${achievement.Required} Star Salutes`;
				}
				return `Star Salutes`;
			case "creature":
				return `Saved ${achievement.CreatureName || "A Creature"}`;
			case "secretSalutes":
				if (achievement.IsComplete) {
					return `Completed All Secret Salutes`;
				}
				return `Secret Salutes`;
			case "contestWin":
				return `Won ${achievement.ContestName}`;
			case "firstGlow":
				return `Created My First Glow Post!`;
			default:
				return "New Achievement Unlocked";
		}
	};

	// Helper function to get achievement body
	const getAchievementBody = (achievement) => {
		// Create detailed description based on achievement type
		switch (achievement.type) {
			case "quest":
				if (achievement.IsComplete) {
					return `I completed the ${achievement.QuestName || "quest"} today!`;
				}
				return `Complete all milestones to unlock this achievement`;
			case "star":
				if (achievement.IsComplete) {
					if (achievement.SalutesSent > achievement.Required) {
						const extra = achievement.SalutesSent - achievement.Required;
						return `I went above and beyond by sending ${achievement.SalutesSent} star salutes (${extra} more than required) to recognize others' amazing work!`;
					}
					return `I sent ${achievement.SalutesSent} star salute${achievement.SalutesSent !== 1 ? "s" : ""} to recognize others' amazing work!`;
				}
				return `Send ${achievement.Required} star salutes to unlock this achievement`;
			case "creature":
				return `I saved ${achievement.CreatureName || "a new beamkin"} and invited them to my santuary!`;
			case "secretSalutes":
				if (achievement.IsComplete) {
					return `I completed all my secret salute cards for the day and earned ${achievement.LumensEarned} lumens!`;
				}
				return `Complete all secret salute cards to unlock this achievement`;
			case "contestWin":
				return `I won the ${achievement.ContestName} contest today!`;
			case "firstGlow": {
				const glowTitle = achievement.Title
					? ` - "${achievement.Title.trim()}"`
					: "";
				return `I created my very first Glow post today${glowTitle}! I shared a good deed I did for someone else and earned ${achievement.LumensEarned || 0} lumens for spreading positivity!`;
			}
			default:
				return "I unlocked a new achievement!";
		}
	};

	// Format duration helper function
	const formatDuration = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Calculate time remaining for an event
	const getTimeRemaining = (endTime) => {
		if (!endTime) {
			return null;
		}

		const now = new Date();
		const end = new Date(endTime);
		const diff = end - now;

		if (diff <= 0) {
			return "Ended";
		}

		const days = Math.floor(diff / (1000 * 60 * 60 * 24));
		const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
		const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

		if (days > 0) {
			return `${days}d ${hours}h left`;
		} else if (hours > 0) {
			return `${hours}h ${minutes}m left`;
		}
		return `${minutes}m left`;
	};

	// Get event type icon component
	const getEventTypeIcon = (eventType) => {
		const iconClass = "w-5 h-5 text-white";
		switch (eventType?.toLowerCase()) {
			case "challenge":
				return <Flag className={iconClass} />;
			case "mission":
				return <span className="text-2xl">🚀</span>;
			case "quest":
				return <span className="text-2xl">⚔️</span>;
			case "competition":
				return <span className="text-2xl">🏆</span>;
			case "event":
				return <span className="text-2xl">📅</span>;
			default:
				return <span className="text-2xl">⭐</span>;
		}
	};

	// Checks if video is recording or previewing
	const isVideo = isVideoRecording || prepRecord;

	if (userStore.getAccessLevel() < 2) {
		return (
			<Card className="mb-6 border border-border bg-card shadow-sm dark:border-border dark:bg-card">
				<CardHeader className="relative pb-2">
					<button
						onClick={onClose}
						className="absolute -right-2 top-4 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
						aria-label="Close"
					>
						<X className="h-5 w-5" />
					</button>
				</CardHeader>
				<CardContent className="pt-2">
					<p className="mb-4">
						Welcome to the newsfeed! Here, members post about positive impact
						they are making in the real world. Every post you see has been
						verified by our party. Members earn points for every verified post.
						Create an account to share your own achievements or interact with
						existing posts!
					</p>
					<Button
						onClick={() =>
							navigate("/story-landing?openModal=receive-transmission")
						}
						className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
					>
						Join the Mission
					</Button>
				</CardContent>
			</Card>
		);
	}
	return (
		<>
			<Card
				className={`relative mb-4 min-w-0 overflow-hidden rounded-2xl border-2 bg-white shadow-lg dark:bg-gray-800 sm:mb-6 ${borderColor}`}
			>
				{contests.length > 0 ? (
					<CardHeader className="relative rounded-t-2xl border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 px-2 py-2 dark:border-gray-700 dark:from-purple-900/20 dark:to-pink-900/20 sm:px-3 sm:py-3">
						{/* Close button - Top right corner */}
						{onClose ? (
							<button
								onClick={onClose}
								className="absolute right-2 top-2 z-50 flex h-6 w-6 items-center justify-center rounded-full border border-red-600 bg-red-500 font-bold text-white shadow-lg transition-colors hover:border-red-700 hover:bg-red-600"
								aria-label="Close"
							>
								<X size={14} className="text-white" />
							</button>
						) : null}
						<div className="flex w-full min-w-0 items-center justify-between gap-2 sm:gap-4">
							{contests.length > 1 && (
								<button
									onClick={() => handleContestChange(contestIndex - 1)}
									className="mt-5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-purple-300 bg-white font-bold text-purple-600 shadow-sm transition-colors hover:bg-purple-100 dark:border-purple-600 dark:bg-gray-700 dark:text-purple-400 dark:hover:bg-purple-800"
								>
									‹
								</button>
							)}
							<div className="min-w-0 flex-1 text-center">
								<div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 sm:mb-2 sm:text-sm">
									Posting to
								</div>
								<div
									className={`truncate text-xl font-black tracking-tight sm:text-3xl ${
										contests[contestIndex].toLowerCase() === "glow game"
											? "text-orange-600 drop-shadow-[0_2px_8px_rgba(251,146,60,0.6)] dark:text-orange-300"
											: "bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(168,85,247,0.4)]"
									}`}
								>
									{contests[contestIndex]}
								</div>
							</div>
							{contests.length > 1 && (
								<button
									onClick={() => handleContestChange(contestIndex + 1)}
									className="mt-5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-purple-300 bg-white font-bold text-purple-600 shadow-sm transition-colors hover:bg-purple-100 dark:border-purple-600 dark:bg-gray-700 dark:text-purple-400 dark:hover:bg-purple-800"
								>
									›
								</button>
							)}
						</div>
					</CardHeader>
				) : null}

				{/* Active Mission Banner - Hide for "Glow Game" */}
				{contests.length > 0 &&
				contests[contestIndex].toLowerCase() !== "glow game" &&
				activeEvent ? (
					<div className="mx-3 mb-0 mt-3 min-w-0 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-100 to-yellow-100 p-3 shadow-md dark:border-amber-700 dark:from-amber-900/30 dark:to-yellow-900/30 sm:mx-6 sm:mt-4 sm:p-4">
						<div className="flex items-start gap-3">
							<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-md">
								{getEventTypeIcon(activeEvent.EventType)}
							</div>
							<div className="flex-1">
								<div className="mb-1 flex items-center justify-between gap-2">
									<div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
										{activeEvent.EventType || "Event"} • Active Now
									</div>
									{activeEvent.EndTime ? (
										<div className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
											⏱️ {getTimeRemaining(activeEvent.EndTime)}
										</div>
									) : null}
								</div>
								<div className="text-sm font-bold text-gray-900 dark:text-white">
									{activeEvent.EventTitle ||
										activeEvent.EventName ||
										"Current Event"}
								</div>
								{activeEvent.EventDescription ? (
									<div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
										{activeEvent.EventDescription}
									</div>
								) : null}
							</div>
						</div>
					</div>
				) : null}

				{/* Preview mode banner for pride posts */}
				{showPostPreview ? (
					<div className="mx-3 mb-0 mt-3 min-w-0 rounded-xl border-2 border-purple-500 bg-purple-100 p-3 shadow-md dark:bg-purple-900/30 sm:mx-6 sm:mt-4 sm:p-4">
						<div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
							<Award className="h-5 w-5" />
							<span className="font-semibold">Preview Mode</span>
						</div>
						<div className="mt-1 text-sm text-purple-600 dark:text-purple-400">
							Review your post below. Click "Post" to share or "Cancel" to go
							back and choose a different achievement.
						</div>
					</div>
				) : null}

				<CardContent className="min-w-0 px-2 pb-2 pt-2 sm:px-3 sm:pb-3 sm:pt-3">
					{/* Post Type Selector - Show with descriptions when no post type selected and no media */}
					{!selectedPostType && !mediaPreview ? (
						<div className="py-4 sm:py-6">
							{contests[contestIndex]?.toLowerCase() === "glow game" ||
							contests[contestIndex]?.toLowerCase() === "the glow game" ? (
								// Show all three post types with descriptions for Glow Game
								<div className="space-y-3">
									{/* Glow Button */}
									<button
										onClick={() => {
											if (
												selectedPostType === "glow" &&
												!mediaFile &&
												!mediaPreview
											) {
												setShowCaptureSelector(true);
											} else {
												onPostTypeSelect && onPostTypeSelect("glow");
											}
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600 ${
											selectedPostType === "glow"
												? "ring-4 ring-yellow-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Star size={24} className="text-yellow-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Glow Post
											</h3>
											<p className="text-sm text-yellow-100">
												Share a kind act you did for others
											</p>
										</div>
										<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
											<div className="relative h-4 w-4">
												<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
												<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
											</div>
											<span className="text-sm font-bold text-white">
												+1-15
											</span>
										</div>
									</button>

									{/* Proud Button */}
									<button
										onClick={() => {
											onPostTypeSelect && onPostTypeSelect("pride");
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 p-4 shadow-lg transition-all hover:scale-105 hover:from-purple-600 hover:to-purple-700 ${
											selectedPostType === "pride"
												? "ring-4 ring-purple-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Award size={24} className="text-purple-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Proud Post
											</h3>
											<p className="text-sm text-purple-100">
												Celebrate your achievements
											</p>
										</div>
									</button>

									{/* Grateful Button */}
									<button
										onClick={() => {
											if (
												selectedPostType === "grateful" &&
												!mediaFile &&
												!mediaPreview
											) {
												setShowCaptureSelector(true);
											} else {
												onPostTypeSelect && onPostTypeSelect("grateful");
											}
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 p-4 shadow-lg transition-all hover:scale-105 hover:from-green-600 hover:to-green-700 ${
											selectedPostType === "grateful"
												? "ring-4 ring-green-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Heart size={24} className="text-green-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Grateful Post
											</h3>
											<p className="text-sm text-green-100">
												Express gratitude to someone special
											</p>
										</div>
										<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-red-500/30 px-3 py-2 backdrop-blur-sm">
											<div className="relative h-4 w-4">
												<div className="absolute inset-0 animate-pulse rounded-full bg-red-200 blur-sm" />
												<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-red-100 to-red-200" />
											</div>
											<span className="text-sm font-bold text-white">-2</span>
										</div>
									</button>
								</div>
							) : (
								// Show Submit for Contest button for other contests
								<button
									onClick={() => {
										if (!mediaFile && !mediaPreview) {
											setShowCaptureSelector(true);
										}
										if (onPostTypeSelect) {
											onPostTypeSelect("glow");
										}
									}}
									className="flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600"
								>
									<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
										<Star size={24} className="text-yellow-500" />
									</div>
									<div className="flex-1 text-left">
										<h3 className="text-lg font-bold text-white">
											Submit for Contest
										</h3>
										<p className="text-sm text-yellow-100">
											Your submission will also be graded for glow
										</p>
									</div>
									<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
										<div className="relative h-4 w-4">
											<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
											<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
										</div>
										<span className="text-sm font-bold text-white">+1-15</span>
									</div>
								</button>
							)}
						</div>
					) : !mediaPreview &&
					  selectedPostType &&
					  selectedPostType !== "pride" ? (
						// Show post type selector when post type is selected but no media (except for pride which shows achievement list)
						<div className="py-4 sm:py-6">
							{contests[contestIndex]?.toLowerCase() === "glow game" ||
							contests[contestIndex]?.toLowerCase() === "the glow game" ? (
								// Show all three post types for Glow Game in vertical list
								<div className="space-y-3">
									{/* Glow Button */}
									<button
										onClick={() => {
											if (
												selectedPostType === "glow" &&
												!mediaFile &&
												!mediaPreview
											) {
												setShowCaptureSelector(true);
											} else {
												onPostTypeSelect && onPostTypeSelect("glow");
											}
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600 ${
											selectedPostType === "glow"
												? "ring-4 ring-yellow-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Star size={24} className="text-yellow-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Glow Post
											</h3>
											<p className="text-sm text-yellow-100">
												Share a kind act you did for others
											</p>
										</div>
										<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
											<div className="relative h-4 w-4">
												<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
												<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
											</div>
											<span className="text-sm font-bold text-white">
												+1-15
											</span>
										</div>
									</button>

									{/* Proud Button */}
									<button
										onClick={() => {
											onPostTypeSelect && onPostTypeSelect("pride");
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 p-4 shadow-lg transition-all hover:scale-105 hover:from-purple-600 hover:to-purple-700 ${
											selectedPostType === "pride"
												? "ring-4 ring-purple-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Award size={24} className="text-purple-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Proud Post
											</h3>
											<p className="text-sm text-purple-100">
												Celebrate your achievements
											</p>
										</div>
									</button>

									{/* Grateful Button */}
									<button
										onClick={() => {
											if (
												selectedPostType === "grateful" &&
												!mediaFile &&
												!mediaPreview
											) {
												setShowCaptureSelector(true);
											} else {
												onPostTypeSelect && onPostTypeSelect("grateful");
											}
										}}
										className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 p-4 shadow-lg transition-all hover:scale-105 hover:from-green-600 hover:to-green-700 ${
											selectedPostType === "grateful"
												? "ring-4 ring-green-300"
												: ""
										}`}
									>
										<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
											<Heart size={24} className="text-green-500" />
										</div>
										<div className="flex-1 text-left">
											<h3 className="text-lg font-bold text-white">
												Create Grateful Post
											</h3>
											<p className="text-sm text-green-100">
												Express gratitude to someone special
											</p>
										</div>
										<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-red-500/30 px-3 py-2 backdrop-blur-sm">
											<div className="relative h-4 w-4">
												<div className="absolute inset-0 animate-pulse rounded-full bg-red-200 blur-sm" />
												<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-red-100 to-red-200" />
											</div>
											<span className="text-sm font-bold text-white">-2</span>
										</div>
									</button>
								</div>
							) : (
								// Show Submit for Contest button for other contests
								<button
									onClick={() => {
										if (!mediaFile && !mediaPreview) {
											setShowCaptureSelector(true);
										}
									}}
									className="flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600"
								>
									<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
										<Star size={24} className="text-yellow-500" />
									</div>
									<div className="flex-1 text-left">
										<h3 className="text-lg font-bold text-white">
											Submit for Contest
										</h3>
										<p className="text-sm text-yellow-100">
											Your submission will also be graded for glow
										</p>
									</div>
									<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
										<div className="relative h-4 w-4">
											<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
											<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
										</div>
										<span className="text-sm font-bold text-white">+1-15</span>
									</div>
								</button>
							)}
						</div>
					) : null}

					{/* Post composer - Full width */}
					<div className="w-full min-w-0">
						{/* Show achievement list for proud posts */}
						{selectedPostType === "pride" && !showPostPreview ? (
							<div className="mb-3">
								{/* Back button to return to post type selection */}
								<Button
									onClick={() => {
										onPostTypeSelect && onPostTypeSelect(null);
										setSelectedProudOption(null);
									}}
									variant="outline"
									size="sm"
									className="mb-3 border-purple-500 bg-white text-purple-600 hover:bg-purple-50 dark:border-purple-400 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-purple-900/20"
								>
									<ArrowLeft size={16} className="mr-1" />
									Back to Post Types
								</Button>
								<div className="mb-3 text-center">
									<h3 className="text-sm font-bold text-purple-700 dark:text-purple-400">
										Choose an Achievement to Share
									</h3>
								</div>

								{/* Container with darker background */}
								<div className="rounded-xl bg-gray-100 p-3 dark:bg-gray-900/60">
									{isLoadingProudOptions ? (
										<div className="flex items-center justify-center py-6">
											<div className="text-sm text-muted-foreground dark:text-muted-foreground">
												Loading achievements...
											</div>
										</div>
									) : !Array.isArray(proudOptions) ||
									  proudOptions.length === 0 ? (
										<div className="rounded-lg bg-white p-3 text-center dark:bg-gray-800">
											<div className="text-xs text-muted-foreground dark:text-muted-foreground">
												No achievements available to share at this time.
												Complete quests and milestones to unlock achievements!
											</div>
										</div>
									) : (
										<div className="scrollbar-hidden max-h-80 space-y-3 overflow-y-auto">
											{Array.isArray(proudOptions) &&
												[...proudOptions]
													.sort((a, b) => {
														// Sort completed (IsComplete = true) to the top
														if (a.IsComplete && !b.IsComplete) {
															return -1;
														}
														if (!a.IsComplete && b.IsComplete) {
															return 1;
														}
														return 0;
													})
													.map((option, index) => {
														const isDisabled = !option.IsComplete;
														return (
															<div
																key={index}
																className={`w-full rounded-xl border p-4 text-left shadow-sm transition-all ${
																	isDisabled
																		? "cursor-not-allowed border-l-4 border-blue-400 border-y-blue-200 border-r-blue-200 bg-blue-50 dark:border-y-blue-600 dark:border-r-blue-600 dark:bg-blue-900/20"
																		: "border-l-4 border-purple-500 border-y-purple-400 border-r-purple-400 bg-purple-500 dark:border-purple-400 dark:bg-purple-500"
																}`}
															>
																<div className="w-full">
																	<div
																		className={`text-base font-bold ${isDisabled ? "text-blue-700 dark:text-blue-300" : "text-white"}`}
																	>
																		{getAchievementTitle(option)}
																	</div>
																	<div
																		className={`mt-2 text-sm ${isDisabled ? "text-blue-600 dark:text-blue-400" : "text-purple-100"}`}
																	>
																		{getAchievementBody(option)}
																	</div>

																	{/* Post button for completed achievements */}
																	{!isDisabled && (
																		<Button
																			onClick={(e) => {
																				e.stopPropagation();
																				setSelectedProudOption(option);
																				const achievementBody =
																					getAchievementBody(option);
																				setTitle("");
																				setContent(achievementBody);
																				setShowPostPreview(true);
																			}}
																			size="sm"
																			className="mt-3 w-full bg-white font-semibold text-purple-600 shadow-md hover:bg-purple-50"
																		>
																			Post This Achievement
																		</Button>
																	)}

																	{/* Progress bars for incomplete achievements */}
																	{isDisabled ? (
																		<div className="mt-2">
																			{option.type === "secretSalutes" && (
																				<>
																					<div className="mb-1 flex justify-between text-xs">
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							Progress
																						</span>
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							{option.Completed}/{option.Total}
																						</span>
																					</div>
																					<div className="mb-2 h-2 w-full rounded-full bg-blue-200 dark:bg-blue-800/50">
																						<div
																							className="h-2 rounded-full bg-purple-500 transition-all"
																							style={{
																								width: `${option.Total > 0 ? (option.Completed / option.Total) * 100 : 0}%`,
																							}}
																						/>
																					</div>
																					<button
																						onClick={(e) => {
																							e.stopPropagation();
																							navigate("/secret-salute");
																						}}
																						className="mt-2 w-full rounded-md bg-purple-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-purple-600"
																					>
																						Go to Secret Salutes
																					</button>
																				</>
																			)}
																			{option.type === "star" && (
																				<>
																					<div className="mb-1 flex justify-between text-xs">
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							Progress
																						</span>
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							{option.SalutesSent}/
																							{option.Required}
																						</span>
																					</div>
																					<div className="mb-2 h-2 w-full rounded-full bg-blue-200 dark:bg-blue-800/50">
																						<div
																							className="h-2 rounded-full bg-yellow-500 transition-all"
																							style={{
																								width: `${option.Required > 0 ? (option.SalutesSent / option.Required) * 100 : 0}%`,
																							}}
																						/>
																					</div>
																					<button
																						onClick={(e) => {
																							e.stopPropagation();
																							navigate("/starsalute");
																						}}
																						className="mt-2 w-full rounded-md bg-yellow-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-yellow-600"
																					>
																						Go to Star Salutes
																					</button>
																				</>
																			)}
																			{option.type === "quest" && (
																				<>
																					<div className="mb-1 flex justify-between text-xs">
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							Milestones
																						</span>
																						<span className="font-medium text-blue-600 dark:text-blue-400">
																							{option.MilestonesCompleted}/
																							{option.TotalMilestones}
																						</span>
																					</div>
																					<div className="mb-2 h-2 w-full rounded-full bg-blue-200 dark:bg-blue-800/50">
																						<div
																							className="h-2 rounded-full bg-green-500 transition-all"
																							style={{
																								width: `${option.TotalMilestones > 0 ? (option.MilestonesCompleted / option.TotalMilestones) * 100 : 0}%`,
																							}}
																						/>
																					</div>
																					<button
																						onClick={(e) => {
																							e.stopPropagation();
																							navigate(
																								"/choose-quest?questID=3&simple=1"
																							);
																						}}
																						className="mt-2 w-full rounded-md bg-green-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-600"
																					>
																						Go to Quests
																					</button>
																				</>
																			)}
																		</div>
																	) : null}
																</div>
															</div>
														);
													})}
										</div>
									)}
								</div>
							</div>
						) : selectedPostType !== "pride" || showPostPreview ? (
							<>
								{/* Insufficient lumens message for grateful posts */}
								{selectedPostType === "grateful" &&
									(userStore.user?.CurrentLumens || 0) < 2 && (
										<div className="mb-3 rounded-lg border-2 border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
											<div className="mb-2 flex items-center gap-2 text-red-700 dark:text-red-400">
												<Star className="h-5 w-5" />
												<span className="text-sm font-semibold">
													You need at least 2 lumens to post a grateful post.
												</span>
											</div>
											<p className="text-xs text-red-600 dark:text-red-500">
												Complete quests and activities to earn more lumens!
											</p>
										</div>
									)}
							</>
						) : null}

						{/* Creature image preview for pride posts */}
						{showPostPreview && selectedProudOption?.type === "creature" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={(() => {
											const creatureMap = {
												1: `${IMAGE_PATH}/quest/creatures/Jokeymon.png`,
												2: `${IMAGE_PATH}/quest/creatures/Patience.png`,
												3: `${IMAGE_PATH}/quest/creatures/Stevie.png`,
												4: `${IMAGE_PATH}/quest/creatures/bluekin.png`,
												5: `${IMAGE_PATH}/quest/creatures/lightkin.png`,
											};
											return creatureMap[selectedProudOption.CreatureNumber];
										})()}
										alt={selectedProudOption.CreatureName || "Creature"}
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute -right-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Stars image preview for star salute pride posts */}
						{showPostPreview && selectedProudOption?.type === "star" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/stars.jpg`}
										alt="Star Salute"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Quest complete image preview for quest completion pride posts */}
						{showPostPreview && selectedProudOption?.type === "quest" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/questComplete.jpg`}
										alt="Quest Complete"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Secret salutes image preview for secret salutes completion pride posts */}
						{showPostPreview &&
						selectedProudOption?.type === "secretSalutes" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/secretSalutes.jpg`}
										alt="Secret Salutes Complete"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* First Glow image preview for first glow post pride achievement */}
						{showPostPreview && selectedProudOption?.type === "firstGlow" ? (
							<>
								<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
									<img
										src={`${IMAGE_PATH}/newsfeed/firstglow.jpg`}
										alt="First Glow Post"
										className="h-full w-full object-cover"
									/>
									{/* Post Type Badge - Top Left */}
									<div className="absolute left-2 top-2 z-10 rounded-full bg-purple-500 px-4 py-2 text-sm font-bold text-white">
										<div className="flex items-center gap-2">
											<Award size={16} />
											<span>Proud Post</span>
										</div>
									</div>
								</div>
								{/* Caption text below image */}
								{content ? (
									<div className="mt-3 px-2">
										<p className="text-sm leading-relaxed text-foreground">
											{content}
										</p>
									</div>
								) : null}
							</>
						) : null}

						{/* Error display */}
						{error ? (
							<div className="mb-2 text-sm text-red-500 dark:text-red-400">
								{error}
							</div>
						) : null}

						{prepRecord && !mediaPreview ? (
							<div className="flex h-60 w-full items-end justify-center gap-2 rounded border border-border bg-muted/20 p-4 dark:border-border dark:bg-muted/10">
								<div className="relative mb-3 mt-2">
									{mediaType === "image" && (
										<img
											src={mediaPreview}
											alt="Upload preview"
											className="max-h-60 w-full rounded-md object-contain"
										/>
									)}

									{(mediaType === "video" ||
										mediaType === "video-recording") && (
										<div className="relative">
											<video
												key={
													mediaType === "video-recording"
														? "recording"
														: "preview"
												}
												ref={
													mediaType === "video-recording" ? videoRef : undefined
												}
												className="max-h-60 w-full rounded-md border border-border dark:border-border"
												autoPlay={mediaType === "video-recording"}
												muted={mediaType === "video-recording"}
												controls={mediaType === "video"}
												src={mediaType === "video" ? mediaPreview : undefined}
												playsInline={mediaType === "video-recording"}
												onLoadedMetadata={(event) => {
													if (mediaType === "video-recording" && mediaPreview) {
														// Ensure the video plays when metadata is loaded
														const videoElement = event.target;
														videoElement.play().catch(console.error);
													}
												}}
												onCanPlay={(event) => {
													if (mediaType === "video-recording" && mediaPreview) {
														// Ensure the video plays when it's ready
														const videoElement = event.target;
														if (videoElement.paused) {
															videoElement.play().catch(console.error);
														}
													}
												}}
											/>
											{mediaType === "video-recording" && (
												<div className="absolute left-2 top-2 flex items-center rounded-md bg-red-500 px-2 py-1 text-sm text-white">
													<span className="mr-2 animate-pulse">●</span>
													Recording {formatDuration(recordingDuration)}
												</div>
											)}
										</div>
									)}

									{mediaType === "audio" && (
										<div className="w-full rounded-md border border-border bg-muted/50 p-3 dark:border-border dark:bg-muted/30">
											<audio
												src={mediaPreview}
												controls
												className="mt-2 w-full"
											/>
											{recordingDuration > 0 && (
												<div className="mt-1 text-sm text-muted-foreground dark:text-muted-foreground">
													Duration: {formatDuration(recordingDuration)}
												</div>
											)}
										</div>
									)}

									<button
										onClick={handleRemoveMedia}
										className="absolute right-2 top-2 rounded-full bg-black bg-opacity-60 p-1 transition-all hover:bg-opacity-80"
										aria-label="Remove media"
									>
										<X size={16} className="text-white" />
									</button>
								</div>
								<Button
									onClick={() => {
										setIsVideoSaving(true);
										setPrepRecord(false);
									}}
									className="bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
								>
									Start Recording
								</Button>
							</div>
						) : null}

						{/* Post Preview - Show media as post body */}
						{mediaPreview && mediaType !== "audio" ? (
							<div className="relative mb-3 mt-2">
								{/* Video Preview - Fullscreen with inputs at bottom */}
								{(mediaType === "video" || mediaType === "video-recording") && (
									<div className="relative aspect-[9/16] w-full overflow-hidden rounded-lg bg-black">
										<video
											key={
												mediaType === "video-recording"
													? "recording"
													: "preview"
											}
											ref={
												mediaType === "video-recording" ? videoRef : undefined
											}
											className="absolute inset-0 h-full w-full object-contain"
											autoPlay
											muted
											loop
											playsInline
											controls={false}
											src={mediaType === "video" ? mediaPreview : undefined}
											onLoadedMetadata={(event) => {
												if (mediaType === "video-recording" && mediaPreview) {
													const videoElement = event.target;
													videoElement.play().catch(console.error);
												}
											}}
											onCanPlay={(event) => {
												if (mediaType === "video-recording" && mediaPreview) {
													const videoElement = event.target;
													if (videoElement.paused) {
														videoElement.play().catch(console.error);
													}
												}
											}}
										/>
										{/* Post Type Badge - Top Left */}
										{selectedPostType !== "pride" && (
											<div
												className={`absolute left-4 top-4 z-20 rounded-full px-4 py-2 text-sm font-bold ${
													selectedPostType === "grateful"
														? "bg-green-500 text-white"
														: "bg-yellow-500 text-white"
												}`}
											>
												{selectedPostType === "grateful" ? (
													<div className="flex items-center gap-2">
														<Heart size={16} />
														<span>Grateful</span>
													</div>
												) : (
													<div className="flex items-center gap-2">
														<Star size={16} />
														<span>Create Glow Post</span>
													</div>
												)}
											</div>
										)}
										{mediaType === "video-recording" && (
											<div className="absolute left-4 top-14 z-20 flex items-center rounded-md bg-red-500 px-3 py-1 text-sm text-white">
												<span className="mr-2 animate-pulse">●</span>
												Recording {formatDuration(recordingDuration)}
											</div>
										)}
										{/* Remove button */}
										<button
											onClick={handleRemoveMedia}
											className="absolute right-4 top-4 z-20 rounded-full bg-black bg-opacity-60 p-2 transition-all hover:bg-opacity-80"
											aria-label="Remove media"
										>
											<X size={20} className="text-white" />
										</button>
										{/* Title and Body Inputs overlay at bottom */}
										{(mediaFile || mediaPreview || showPostPreview) &&
										!(
											selectedPostType === "grateful" &&
											(userStore.user?.CurrentLumens || 0) < 2
										) ? (
											<div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/95 via-black/90 to-transparent p-4">
												<div className="relative">
													<Textarea
														ref={textareaRef}
														placeholder={
															contests[contestIndex]?.toLowerCase() !==
																"glow game" &&
															contests[contestIndex]?.toLowerCase() !==
																"the glow game"
																? "What did you do for this contest? Your submission will also be graded for glow"
																: selectedPostType === "grateful"
																	? "Who are you grateful for?"
																	: selectedPostType === "glow"
																		? "What unexpected joy did you bring to others?"
																		: `What's on your mind, ${userStore.user.Username?.split(" ")[0] || "there"}?`
														}
														className={`min-h-20 w-full resize-none border-2 bg-white/50 dark:bg-gray-800/50 ${
															selectedPostType === "grateful"
																? "border-green-500 dark:border-green-500"
																: selectedPostType === "pride"
																	? "border-purple-500 dark:border-purple-500"
																	: "border-yellow-500 dark:border-yellow-500"
														} pr-12 text-foreground placeholder:text-gray-600 dark:text-foreground dark:placeholder:text-gray-400 ${
															showPostPreview
																? "disabled:text-foreground disabled:opacity-100 disabled:dark:text-foreground"
																: ""
														}`}
														value={content}
														onChange={(e) => setContent(e.target.value)}
														disabled={
															isPosting ||
															isRecording ||
															isVideo ||
															showPostPreview
														}
													/>
													{/* Voice-to-text microphone button */}
													<button
														type="button"
														onClick={() => {
															if (isListening) {
																stopSpeechRecognition();
															} else {
																startSpeechRecognition();
															}
														}}
														disabled={
															isPosting ||
															isRecording ||
															isVideo ||
															showPostPreview
														}
														className={`absolute bottom-2 right-2 rounded-full border-2 border-red-500 p-2 transition-colors ${
															isListening
																? "animate-pulse border-red-600 bg-red-500 text-white hover:bg-red-600"
																: "border-red-500 bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
														} ${
															isPosting ||
															isRecording ||
															isVideo ||
															showPostPreview
																? "cursor-not-allowed opacity-50"
																: "cursor-pointer"
														}`}
														aria-label={
															isListening
																? "Stop voice input"
																: "Start voice input"
														}
														title={
															isListening
																? "Stop voice input"
																: "Start voice input"
														}
													>
														<Mic
															size={18}
															className={isListening ? "animate-pulse" : ""}
														/>
													</button>
												</div>
											</div>
										) : null}
									</div>
								)}

								{/* Image Preview - Square cropped with inputs below */}
								{mediaType === "image" && (
									<>
										<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
											<img
												src={mediaPreview}
												alt="Upload preview"
												className="h-full w-full object-cover"
											/>
											{/* Post Type Badge - Top Left */}
											{selectedPostType !== "pride" && (
												<div
													className={`absolute left-2 top-2 z-10 rounded-full px-4 py-2 text-sm font-bold ${
														selectedPostType === "grateful"
															? "bg-green-500 text-white"
															: "bg-yellow-500 text-white"
													}`}
												>
													{selectedPostType === "grateful" ? (
														<div className="flex items-center gap-2">
															<Heart size={16} />
															<span>Grateful</span>
														</div>
													) : (
														<div className="flex items-center gap-2">
															<Star size={16} />
															<span>Create Glow Post</span>
														</div>
													)}
												</div>
											)}
											{/* Remove button */}
											<button
												onClick={handleRemoveMedia}
												className="absolute right-2 top-2 z-10 rounded-full bg-black bg-opacity-60 p-2 transition-all hover:bg-opacity-80"
												aria-label="Remove media"
											>
												<X size={20} className="text-white" />
											</button>
										</div>
										{/* Body Input below image */}
										{(mediaFile || mediaPreview || showPostPreview) &&
										!(
											selectedPostType === "grateful" &&
											(userStore.user?.CurrentLumens || 0) < 2
										) ? (
											<div className="relative mb-2 mt-3">
												<Textarea
													ref={textareaRef}
													placeholder={
														contests[contestIndex]?.toLowerCase() !==
															"glow game" &&
														contests[contestIndex]?.toLowerCase() !==
															"the glow game"
															? "What did you do for this contest? Your submission will also be graded for glow"
															: selectedPostType === "grateful"
																? "Who are you grateful for?"
																: selectedPostType === "glow"
																	? "What unexpected joy did you bring to others?"
																	: `What's on your mind, ${userStore.user.Username?.split(" ")[0] || "there"}?`
													}
													className={`min-h-24 w-full resize-none border-2 bg-white/50 dark:bg-gray-800/50 ${
														selectedPostType === "grateful"
															? "border-green-500 dark:border-green-500"
															: selectedPostType === "pride"
																? "border-purple-500 dark:border-purple-500"
																: "border-yellow-500 dark:border-yellow-500"
													} pr-12 text-foreground placeholder:text-gray-600 dark:text-foreground dark:placeholder:text-gray-400 ${
														showPostPreview
															? "disabled:text-foreground disabled:opacity-100 disabled:dark:text-foreground"
															: ""
													}`}
													value={content}
													onChange={(e) => setContent(e.target.value)}
													disabled={
														isPosting ||
														isRecording ||
														isVideo ||
														showPostPreview
													}
												/>
												{/* Voice-to-text microphone button */}
												<button
													type="button"
													onClick={() => {
														if (isListening) {
															stopSpeechRecognition();
														} else {
															startSpeechRecognition();
														}
													}}
													disabled={
														isPosting ||
														isRecording ||
														isVideo ||
														showPostPreview
													}
													className={`absolute bottom-2 right-2 rounded-full border-2 border-red-500 p-2 transition-colors ${
														isListening
															? "animate-pulse border-red-600 bg-red-500 text-white hover:bg-red-600"
															: "border-red-500 bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
													} ${
														isPosting ||
														isRecording ||
														isVideo ||
														showPostPreview
															? "cursor-not-allowed opacity-50"
															: "cursor-pointer"
													}`}
													aria-label={
														isListening
															? "Stop voice input"
															: "Start voice input"
													}
													title={
														isListening
															? "Stop voice input"
															: "Start voice input"
													}
												>
													<Mic
														size={18}
														className={isListening ? "animate-pulse" : ""}
													/>
												</button>
											</div>
										) : null}
									</>
								)}
							</div>
						) : null}

						{/* Audio Preview - Waveform and Player */}
						{mediaPreview && mediaType === "audio" ? (
							<div className="relative mb-3 mt-2">
								{/* Hidden audio element */}
								<audio
									ref={audioElementRef}
									src={mediaPreview}
									onLoadedMetadata={(e) => {
										setAudioDuration(e.target.duration);
									}}
									onTimeUpdate={(e) => {
										setAudioCurrentTime(e.target.currentTime);
									}}
									onEnded={() => {
										setIsAudioPlaying(false);
										setAudioCurrentTime(0);
										// Reset waveform visualization
										if (animationFrameRef.current) {
											cancelAnimationFrame(animationFrameRef.current);
											animationFrameRef.current = null;
										}
										setAudioLevels([]);
									}}
									className="hidden"
								/>

								{/* Single card containing both waveform and controls */}
								<div
									className="w-full space-y-4 rounded-2xl border-2 p-4"
									style={{
										backgroundColor: "rgba(0, 0, 0, 0.9)",
										borderColor: "rgba(234, 179, 8, 0.3)",
									}}
								>
									{/* Waveform visualization */}
									<Waveform audioLevels={audioLevels} isRecording={false} />

									{/* Simplified Audio Controls - Progress bar */}
									<div className="space-y-3">
										<div className="flex items-center gap-4">
											{/* Play/Pause Button */}
											<button
												onClick={() => {
													if (audioElementRef.current) {
														if (isAudioPlaying) {
															audioElementRef.current.pause();
															setIsAudioPlaying(false);
														} else {
															audioElementRef.current.play();
															setIsAudioPlaying(true);

															// Restart visualization if it was stopped
															if (
																!animationFrameRef.current &&
																playbackAnalyserRef.current
															) {
																const bufferLength =
																	playbackAnalyserRef.current.frequencyBinCount;
																const dataArray = new Uint8Array(bufferLength);

																const visualize = () => {
																	if (!playbackAnalyserRef.current) {
																		return;
																	}

																	playbackAnalyserRef.current.getByteTimeDomainData(
																		dataArray
																	);
																	const waveformData = Array.from(dataArray);
																	setAudioLevels(waveformData);

																	animationFrameRef.current =
																		requestAnimationFrame(visualize);
																};

																visualize();
															}
														}
													}
												}}
												className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-lg transition-all"
												style={{
													borderColor: "#fbbf24",
													backgroundColor: "#fbbf24",
													boxShadow: isAudioPlaying
														? "0 0 20px rgba(251, 191, 36, 0.6)"
														: "0 0 10px rgba(251, 191, 36, 0.3)",
												}}
												aria-label={
													isAudioPlaying ? "Pause audio" : "Play audio"
												}
											>
												{isAudioPlaying ? (
													<Pause size={20} fill="white" color="white" />
												) : (
													<Play size={20} fill="white" color="white" />
												)}
											</button>

											{/* Progress Bar - Interactive Seek Bar */}
											<div className="flex-1">
												<div
													className="group relative h-2 cursor-pointer rounded-full bg-gray-700"
													onClick={(e) => {
														const rect =
															e.currentTarget.getBoundingClientRect();
														const x = e.clientX - rect.left;
														const percentage = x / rect.width;
														const newTime = percentage * audioDuration;
														if (audioElementRef.current && !isNaN(newTime)) {
															audioElementRef.current.currentTime = newTime;
															setAudioCurrentTime(newTime);
														}
													}}
												>
													{/* Progress Fill */}
													<div
														className="absolute left-0 top-0 h-full rounded-full transition-all duration-100"
														style={{
															width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
															backgroundColor: "#fbbf24",
															boxShadow: "0 0 10px rgba(251, 191, 36, 0.6)",
														}}
													/>
													{/* Draggable Thumb/Dot */}
													<div
														className="absolute h-4 w-4 cursor-grab rounded-full border-2 border-white shadow-lg active:cursor-grabbing group-hover:scale-110"
														style={{
															left: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%`,
															top: "50%",
															backgroundColor: "#fbbf24",
															boxShadow: "0 0 10px rgba(251, 191, 36, 0.8)",
															transform: "translate(-50%, -50%)",
															pointerEvents: "auto",
														}}
														onMouseDown={(e) => {
															e.stopPropagation();
															e.preventDefault();
															const handleMouseMove = (moveEvent) => {
																const rect =
																	e.currentTarget.parentElement.getBoundingClientRect();
																const x = Math.max(
																	0,
																	Math.min(
																		moveEvent.clientX - rect.left,
																		rect.width
																	)
																);
																const percentage = x / rect.width;
																const newTime = percentage * audioDuration;
																if (
																	audioElementRef.current &&
																	!isNaN(newTime)
																) {
																	audioElementRef.current.currentTime = newTime;
																	setAudioCurrentTime(newTime);
																}
															};
															const handleMouseUp = () => {
																document.removeEventListener(
																	"mousemove",
																	handleMouseMove
																);
																document.removeEventListener(
																	"mouseup",
																	handleMouseUp
																);
															};
															document.addEventListener(
																"mousemove",
																handleMouseMove
															);
															document.addEventListener(
																"mouseup",
																handleMouseUp
															);
														}}
														onTouchStart={(e) => {
															e.stopPropagation();
															e.preventDefault();
															const handleTouchMove = (moveEvent) => {
																moveEvent.preventDefault();
																const touch = moveEvent.touches[0];
																const rect =
																	e.currentTarget.parentElement.getBoundingClientRect();
																const x = Math.max(
																	0,
																	Math.min(
																		touch.clientX - rect.left,
																		rect.width
																	)
																);
																const percentage = x / rect.width;
																const newTime = percentage * audioDuration;
																if (
																	audioElementRef.current &&
																	!isNaN(newTime)
																) {
																	audioElementRef.current.currentTime = newTime;
																	setAudioCurrentTime(newTime);
																}
															};
															const handleTouchEnd = () => {
																document.removeEventListener(
																	"touchmove",
																	handleTouchMove
																);
																document.removeEventListener(
																	"touchend",
																	handleTouchEnd
																);
															};
															document.addEventListener(
																"touchmove",
																handleTouchMove,
																{ passive: false }
															);
															document.addEventListener(
																"touchend",
																handleTouchEnd
															);
														}}
													/>
												</div>
											</div>
										</div>

										{/* Duration Display */}
										<div
											className="flex w-full justify-between px-2 font-mono text-xs font-semibold"
											style={{ color: "#fbbf24" }}
										>
											<div>{formatDuration(Math.floor(audioCurrentTime))}</div>
											<div>{formatDuration(Math.floor(audioDuration))}</div>
										</div>
									</div>
								</div>

								<button
									onClick={handleRemoveMedia}
									className="absolute right-2 top-2 rounded-full bg-black bg-opacity-60 p-1 transition-all hover:bg-opacity-80"
									aria-label="Remove media"
								>
									<X size={16} className="text-white" />
								</button>
							</div>
						) : null}

						{/* Body Input - Show after audio is uploaded */}
						{mediaPreview &&
						mediaType === "audio" &&
						(mediaFile || mediaPreview || showPostPreview) &&
						selectedPostType !== "pride" &&
						!(
							selectedPostType === "grateful" &&
							(userStore.user?.CurrentLumens || 0) < 2
						) ? (
							<div className="relative mb-2 mt-3">
								<Textarea
									ref={textareaRef}
									placeholder={
										contests[contestIndex]?.toLowerCase() !== "glow game" &&
										contests[contestIndex]?.toLowerCase() !== "the glow game"
											? "What did you do for this contest? Your submission will also be graded for glow"
											: selectedPostType === "grateful"
												? "Who are you grateful for?"
												: selectedPostType === "glow"
													? "What unexpected joy did you bring to others?"
													: `What's on your mind, ${userStore.user.Username?.split(" ")[0] || "there"}?`
									}
									className={`min-h-24 w-full resize-none border-2 bg-white/50 dark:bg-gray-800/50 ${
										selectedPostType === "grateful"
											? "border-green-500 dark:border-green-500"
											: selectedPostType === "pride"
												? "border-purple-500 dark:border-purple-500"
												: "border-yellow-500 dark:border-yellow-500"
									} pr-12 text-foreground placeholder:text-gray-600 dark:text-foreground dark:placeholder:text-gray-400 ${
										showPostPreview
											? "disabled:text-foreground disabled:opacity-100 disabled:dark:text-foreground"
											: ""
									}`}
									value={content}
									onChange={(e) => setContent(e.target.value)}
									disabled={
										isPosting || isRecording || isVideo || showPostPreview
									}
								/>
								{/* Voice-to-text microphone button */}
								<button
									type="button"
									onClick={() => {
										if (isListening) {
											stopSpeechRecognition();
										} else {
											startSpeechRecognition();
										}
									}}
									disabled={
										isPosting || isRecording || isVideo || showPostPreview
									}
									className={`absolute bottom-2 right-2 rounded-full border-2 border-red-500 p-2 transition-colors ${
										isListening
											? "animate-pulse border-red-600 bg-red-500 text-white hover:bg-red-600"
											: "border-red-500 bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
									} ${
										isPosting || isRecording || isVideo || showPostPreview
											? "cursor-not-allowed opacity-50"
											: "cursor-pointer"
									}`}
									aria-label={
										isListening ? "Stop voice input" : "Start voice input"
									}
									title={isListening ? "Stop voice input" : "Start voice input"}
								>
									<Mic
										size={18}
										className={isListening ? "animate-pulse" : ""}
									/>
								</button>
							</div>
						) : null}

						{/* Hidden file inputs */}
						<input
							type="file"
							ref={fileInputRef}
							onChange={(e) => handleFileSelect(e, "image")}
							accept="image/*"
							className="hidden"
							disabled={isPosting}
						/>
						<input
							type="file"
							ref={videoInputRef}
							onChange={(e) => handleFileSelect(e, "video")}
							accept="video/*"
							className="hidden"
							disabled={isPosting}
						/>
						<input
							type="file"
							ref={audioInputRef}
							onChange={(e) => handleFileSelect(e, "audio")}
							accept="audio/*"
							className="hidden"
							disabled={isPosting}
						/>

						{/* Add Media Section - Hide for proud posts, grateful posts without lumens, AND when media is already uploaded */}

						{/* Post button */}
						<div className="mt-3">
							{/* Validation message for missing media */}
							{/* {selectedPostType !== "pride" && !showPostPreview && !(mediaFile || mediaPreview) && (
							<div className="mb-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg text-center">
								<p className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
									⚠️ Please add media (photo, video, or audio) to create your post
								</p>
							</div>
						)} */}

							<div className="flex justify-end">
								{showPostPreview ? (
									<div className="flex gap-2">
										<Button
											onClick={() => {
												setShowPostPreview(false);
												setTitle("");
												setContent("");
											}}
											disabled={isPosting}
											size="sm"
											variant="outline"
											className="flex items-center"
										>
											<X size={16} className="mr-2" />
											Cancel
										</Button>
										{!(
											selectedPostType === "grateful" &&
											(userStore.user?.CurrentLumens || 0) < 2
										) &&
										content &&
										content.trim() ? (
											<Button
												onClick={handleSubmit}
												disabled={isPosting}
												size="sm"
												className="flex items-center border-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md hover:from-purple-700 hover:to-pink-700"
											>
												<Send size={16} className="mr-2" />
												{isPosting ? "Posting..." : "Post"}
											</Button>
										) : null}
									</div>
								) : selectedPostType !== "pride" ? (
									!(
										selectedPostType === "grateful" &&
										(userStore.user?.CurrentLumens || 0) < 2
									) &&
									content &&
									content.trim() && (
										<Button
											onClick={handleSubmit}
											disabled={
												!content ||
												!(mediaFile || mediaPreview) ||
												isRecording ||
												isPosting
											}
											size="sm"
											className="flex items-center border-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md hover:from-purple-700 hover:to-pink-700"
										>
											<Send size={16} className="mr-2" />
											{isPosting ? "Posting..." : "Post"}
										</Button>
									)
								) : null}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Capture Method Selector */}
			{showCaptureSelector ? (
				<CaptureMethodSelector
					onSelect={handleCaptureMethodSelect}
					onClose={() => setShowCaptureSelector(false)}
					postType={selectedPostType}
					isContest={
						contests[contestIndex]?.toLowerCase() !== "glow game" &&
						contests[contestIndex]?.toLowerCase() !== "the glow game"
					}
				/>
			) : null}

			{/* Custom Camera Component */}
			{showCamera ? (
				<Camera
					onCapture={handleCameraCapture}
					onClose={() => {
						setShowCamera(false);
						setAutoTriggerUpload(false);
					}}
					mode={cameraMode}
					showPreview
					autoTriggerUpload={autoTriggerUpload}
					postType={selectedPostType}
					isContest={
						contests[contestIndex]?.toLowerCase() !== "glow game" &&
						contests[contestIndex]?.toLowerCase() !== "the glow game"
					}
					delayInitialization={false}
				/>
			) : null}
		</>
	);
};

export default ComposePost;
